<script setup>
// Criacao da caixa de e-mail em UMA tela.
//
// Antes: o formulario pedia so nome e endereco, criava a caixa em modo
// encaminhamento e ia embora. A caixa aparecia pronta na lista e nao recebia
// nada ate alguem lembrar de abrir Configuracoes e ligar o IMAP — falha que
// nao da pra ver. Agora o provedor, a senha e o teste de conexao moram aqui,
// e a caixa nasce recebendo e enviando. Quem nao tem a senha em maos continua
// podendo criar so com encaminhamento e configurar depois.
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, email as isEmail, requiredIf } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { toast } from 'dashboard/composables/useToast';
import PageHeader from '../../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import {
  EMAIL_PROVIDERS,
  providerNeedsDomain,
  resolveProvider,
  domainFromEmail,
} from '../../helpers/emailProviders';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const uiFlags = useMapGetter('inboxes/getUIFlags');

const channelName = ref('');
const email = ref('');
const modo = ref('connect'); // connect | forward
const providerKey = ref('');
const domain = ref('');
const password = ref('');
const mostrarAvancado = ref(false);
const testando = ref(false);
const resultadoTeste = ref(null); // { ok, mensagem }

// Servidor: nasce do preset e fica editavel (provedor fora da lista, porta
// diferente, servidor proprio). Campos planos de proposito — regra aninhada
// no Vuelidate sobre um ref de objeto nao gera v$.imap.address, e o template
// quebrava inteiro ao ler .$error de undefined.
const imapAddress = ref('');
const imapPort = ref(993);
const imapSsl = ref(true);
const smtpAddress = ref('');
const smtpPort = ref(587);
const smtpEncryption = ref('starttls');

const conectando = computed(() => modo.value === 'connect');
const precisaDominio = computed(() => providerNeedsDomain(providerKey.value));
const notaProvedor = computed(
  () => resolveProvider(providerKey.value, domain.value)?.note || ''
);

const rules = {
  channelName: { required },
  email: { required, isEmail },
  providerKey: { required: requiredIf(conectando) },
  password: { required: requiredIf(conectando) },
  imapAddress: { required: requiredIf(conectando) },
  imapPort: { required: requiredIf(conectando) },
  smtpAddress: { required: requiredIf(conectando) },
  smtpPort: { required: requiredIf(conectando) },
};
const v$ = useVuelidate(rules, {
  channelName,
  email,
  providerKey,
  password,
  imapAddress,
  imapPort,
  smtpAddress,
  smtpPort,
});

// Trocar de provedor (ou digitar o dominio) repoe servidor e porta; o que a
// pessoa tiver ajustado no avancado e substituido de proposito.
const aplicarProvedor = () => {
  const preset = resolveProvider(providerKey.value, domain.value);
  if (!preset) return;

  imapAddress.value = preset.imap.address;
  imapPort.value = preset.imap.port;
  imapSsl.value = preset.imap.ssl;
  smtpAddress.value = preset.smtp.address;
  smtpPort.value = preset.smtp.port;
  smtpEncryption.value = preset.smtp.encryption;
  resultadoTeste.value = null;
  mostrarAvancado.value = preset.manual || mostrarAvancado.value;
};

watch([providerKey, domain], aplicarProvedor);
// O dominio do proprio e-mail ja serve pros provedores que pedem um.
watch(email, valor => {
  if (!domain.value) domain.value = domainFromEmail(valor);
});

const configDoCanal = () => ({
  imap_address: imapAddress.value,
  imap_port: Number(imapPort.value),
  imap_login: email.value,
  imap_password: password.value,
  imap_enable_ssl: imapSsl.value,
  smtp_address: smtpAddress.value,
  smtp_port: Number(smtpPort.value),
  smtp_login: email.value,
  smtp_password: password.value,
});

const testarConexao = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  testando.value = true;
  resultadoTeste.value = null;
  const config = configDoCanal();
  try {
    const entrada = await InboxesAPI.testEmailConnection({
      kind: 'imap',
      config,
    });
    if (!entrada.data.ok) {
      resultadoTeste.value = {
        ok: false,
        mensagem: `${t('INBOX_MGMT.ADD.EMAIL_CHANNEL.SETUP.TEST_IMAP_FAILED')} ${entrada.data.error}`,
      };
      return;
    }

    const saida = await InboxesAPI.testEmailConnection({
      kind: 'smtp',
      config: { ...config, encryption: smtpEncryption.value },
    });
    resultadoTeste.value = saida.data.ok
      ? { ok: true, mensagem: t('INBOX_MGMT.ADD.EMAIL_CHANNEL.SETUP.TEST_OK') }
      : {
          ok: false,
          mensagem: `${t('INBOX_MGMT.ADD.EMAIL_CHANNEL.SETUP.TEST_SMTP_FAILED')} ${saida.data.error}`,
        };
  } catch (erro) {
    resultadoTeste.value = {
      ok: false,
      mensagem: erro?.response?.data?.error || erro.message,
    };
  } finally {
    testando.value = false;
  }
};

const atributosDoCanal = () => {
  const base = { type: 'email', email: email.value };
  if (!conectando.value) return base;

  const config = configDoCanal();
  return {
    ...base,
    ...config,
    imap_enabled: true,
    smtp_enabled: true,
    smtp_domain: domainFromEmail(email.value),
    smtp_enable_starttls_auto: smtpEncryption.value === 'starttls',
    smtp_enable_ssl_tls: smtpEncryption.value === 'ssl',
    smtp_openssl_verify_mode: 'none',
    smtp_authentication: 'login',
    provider: providerKey.value,
  };
};

const criarCaixa = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const canal = await store.dispatch('inboxes/createChannel', {
      name: channelName.value?.trim(),
      channel: atributosDoCanal(),
    });
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: { page: 'new', inbox_id: canal.id },
    });
  } catch (erro) {
    toast.error(
      erro?.message || t('INBOX_MGMT.ADD.EMAIL_CHANNEL.API.ERROR_MESSAGE')
    );
  }
};

const st = chave => t(`INBOX_MGMT.ADD.EMAIL_CHANNEL.SETUP.${chave}`);
</script>

<template>
  <div class="w-full h-full col-span-6 p-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.EMAIL_CHANNEL.TITLE')"
      :header-content="st('DESCRIPTION')"
    />

    <form class="flex flex-col max-w-2xl mx-0" @submit.prevent="criarCaixa()">
      <label :class="{ error: v$.channelName.$error }">
        {{ $t('INBOX_MGMT.ADD.EMAIL_CHANNEL.CHANNEL_NAME.LABEL') }}
        <input
          v-model="channelName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.EMAIL_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
          "
          @blur="v$.channelName.$touch"
        />
        <span v-if="v$.channelName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.EMAIL_CHANNEL.CHANNEL_NAME.ERROR') }}
        </span>
      </label>

      <label :class="{ error: v$.email.$error }">
        {{ $t('INBOX_MGMT.ADD.EMAIL_CHANNEL.EMAIL.LABEL') }}
        <input
          v-model="email"
          type="text"
          :placeholder="$t('INBOX_MGMT.ADD.EMAIL_CHANNEL.EMAIL.PLACEHOLDER')"
          @blur="v$.email.$touch"
        />
        <span v-if="v$.email.$error" class="message">
          {{ st('EMAIL_ERROR') }}
        </span>
      </label>

      <!-- Como a caixa vai funcionar -->
      <fieldset class="p-0 m-0 mt-2 mb-6 border-0">
        <legend class="mb-2 text-sm font-medium text-n-slate-12">
          {{ st('MODE_LEGEND') }}
        </legend>
        <div class="flex flex-col gap-2">
          <label
            v-for="opcao in ['connect', 'forward']"
            :key="opcao"
            class="flex items-start gap-2.5 p-3 border border-solid rounded-xl cursor-pointer transition-colors"
            :class="
              modo === opcao
                ? 'border-n-brand bg-n-brand/5'
                : 'border-n-weak hover:border-n-strong'
            "
          >
            <input
              v-model="modo"
              type="radio"
              :value="opcao"
              class="mt-1 !mb-0"
            />
            <span class="flex flex-col gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ st(opcao === 'connect' ? 'MODE_CONNECT' : 'MODE_FORWARD') }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  st(
                    opcao === 'connect'
                      ? 'MODE_CONNECT_HELP'
                      : 'MODE_FORWARD_HELP'
                  )
                }}
              </span>
            </span>
          </label>
        </div>
      </fieldset>

      <!-- Provedor + senha -->
      <template v-if="conectando">
        <div class="flex flex-wrap gap-4">
          <label
            class="flex-1 min-w-[240px]"
            :class="{ error: v$.providerKey.$error }"
          >
            {{ st('PROVIDER') }}
            <select v-model="providerKey">
              <option value="">{{ st('PROVIDER_PLACEHOLDER') }}</option>
              <option v-for="p in EMAIL_PROVIDERS" :key="p.key" :value="p.key">
                {{ p.label }}
              </option>
            </select>
            <span v-if="v$.providerKey.$error" class="message">
              {{ st('PROVIDER_ERROR') }}
            </span>
          </label>

          <label v-if="precisaDominio" class="flex-1 min-w-[240px]">
            {{ st('DOMAIN') }}
            <input
              v-model="domain"
              type="text"
              :placeholder="st('DOMAIN_PH')"
            />
          </label>
        </div>

        <label :class="{ error: v$.password.$error }">
          {{ st('PASSWORD') }}
          <input
            v-model="password"
            type="password"
            autocomplete="new-password"
            :placeholder="st('PASSWORD_PLACEHOLDER')"
            @blur="v$.password.$touch"
          />
          <p class="help-text">{{ st('PASSWORD_HELP') }}</p>
          <span v-if="v$.password.$error" class="message">
            {{ st('PASSWORD_ERROR') }}
          </span>
        </label>

        <p
          v-if="notaProvedor"
          class="px-3 py-2 mb-4 text-xs border rounded-lg text-n-amber-12 bg-n-amber-2 border-n-amber-6"
        >
          {{ notaProvedor }}
        </p>

        <!-- Servidor: preenchido pelo provedor, aberto pra quem precisa -->
        <button
          type="button"
          class="inline-flex items-center gap-1.5 mb-3 text-xs text-n-slate-11 hover:text-n-slate-12"
          @click="mostrarAvancado = !mostrarAvancado"
        >
          <span
            class="size-3.5"
            :class="
              mostrarAvancado
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
          />
          {{ st('ADVANCED') }}
        </button>

        <div
          v-if="mostrarAvancado"
          class="grid grid-cols-1 gap-4 p-4 mb-4 border rounded-xl sm:grid-cols-2 border-n-weak bg-n-solid-2"
        >
          <label :class="{ error: v$.imapAddress.$error }">
            {{ st('IMAP_ADDRESS') }}
            <input
              v-model="imapAddress"
              type="text"
              :placeholder="st('IMAP_ADDRESS_PH')"
            />
          </label>
          <label :class="{ error: v$.imapPort.$error }">
            {{ st('IMAP_PORT') }}
            <input
              v-model="imapPort"
              type="number"
              :placeholder="st('IMAP_PORT_PH')"
            />
          </label>
          <label :class="{ error: v$.smtpAddress.$error }">
            {{ st('SMTP_ADDRESS') }}
            <input
              v-model="smtpAddress"
              type="text"
              :placeholder="st('SMTP_ADDRESS_PH')"
            />
          </label>
          <label :class="{ error: v$.smtpPort.$error }">
            {{ st('SMTP_PORT') }}
            <input
              v-model="smtpPort"
              type="number"
              :placeholder="st('SMTP_PORT_PH')"
            />
          </label>
          <label class="sm:col-span-2">
            {{ st('ENCRYPTION') }}
            <select v-model="smtpEncryption">
              <option value="starttls">{{ st('ENC_STARTTLS') }}</option>
              <option value="ssl">{{ st('ENC_SSL') }}</option>
            </select>
            <p class="help-text">{{ st('ENCRYPTION_HELP') }}</p>
          </label>
        </div>

        <div class="flex flex-wrap items-center gap-3 mb-6">
          <NextButton
            type="button"
            faded
            slate
            :is-loading="testando"
            :label="st('TEST')"
            @click="testarConexao"
          />
          <span
            v-if="resultadoTeste"
            class="text-xs"
            :class="resultadoTeste.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ resultadoTeste.mensagem }}
          </span>
        </div>
      </template>

      <div>
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.EMAIL_CHANNEL.SUBMIT_BUTTON')"
        />
      </div>
    </form>
  </div>
</template>
